import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { CommentsApi } from './comments-api-context';
import { CommentsApiContext } from './comments-api-context';
import type { CommentsSubject } from './subject/comments-subject';

export type CommentsScope = {
    readonly architectureId: string;
    readonly canCreateThreads?: boolean;
    readonly itemId: string;
    readonly kind?: CommentsSubject['kind'];
    readonly language: string;
    readonly persistence?: CommentsSubject['persistence'];
    readonly requestedVersion: CommentsSubject['transportVersion'];
    readonly version?: CommentsSubject['transportVersion'];
};

export const isCommentsScopeCurrent = (
    storedScope: CommentsScope | null,
    currentScope: CommentsScope | null,
): boolean => {
    if (is.null(storedScope)) {
        return true;
    }
    if (is.null(currentScope)) {
        return false;
    }
    return (
        storedScope.architectureId === currentScope.architectureId &&
        storedScope.itemId === currentScope.itemId &&
        storedScope.kind === currentScope.kind &&
        storedScope.language === currentScope.language &&
        storedScope.requestedVersion === currentScope.requestedVersion &&
        storedScope.version === currentScope.version
    );
};

export const CommentsScopeContext = createContext<CommentsScope | null>(null);

export const BoundCommentsScopeProvider: FunctionComponent<
    PropsWithChildren<{ readonly api: CommentsApi; readonly subject: CommentsSubject }>
> = ({ api, children, subject }) => {
    const value = useMemo<CommentsScope>(
        () => ({
            architectureId: subject.architectureId,
            canCreateThreads: subject.canCreateThreads,
            itemId: subject.subjectId,
            kind: subject.kind,
            language: subject.language,
            persistence: subject.persistence,
            requestedVersion: subject.transportVersion,
            ...(subject.persistence === 'persisted' ? { version: subject.transportVersion } : {}),
        }),
        [subject],
    );

    return (
        <CommentsApiContext.Provider value={api}>
            <CommentsScopeContext.Provider value={value}>{children}</CommentsScopeContext.Provider>
        </CommentsApiContext.Provider>
    );
};

export const useCommentsScope = (): CommentsScope => {
    const value = useContext(CommentsScopeContext);
    if (is.null(value)) {
        throw new Error('useCommentsScope must be used inside a comments scope provider');
    }
    return value;
};

export const useOptionalCommentsScope = (): CommentsScope | null => {
    return useContext(CommentsScopeContext);
};
